"use client";
import React from 'react';
import { motion } from 'framer-motion';
import { useContactModal } from '../../../../contexts/ContactModalContext';

const cards = [
    {
        col: 'md:col-span-2',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="7" stroke="#ceff1a" strokeWidth="1.6"/>
                <path d="M10 6v4l2.5 2.5" stroke="#ceff1a" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
        ),
        text: 'Lead Workz is a performance marketing agency in Bangalore that believes great marketing starts long before the first campaign goes live.',
        highlight: false,
    },
    {
        col: 'md:col-span-3',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="8" cy="8" r="5" stroke="#ceff1a" strokeWidth="1.6"/>
                <path d="M13 13l4 4" stroke="#ceff1a" strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M6 8h4M8 6v4" stroke="#ceff1a" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
        ),
        text: 'Before we launch Google Ads campaigns, Meta Ads campaigns, or optimise landing pages, we focus on understanding the people behind the click. What drives them? What influences their decisions? What creates trust? What ultimately moves them from consideration to action?',
        highlight: false,
    },
    {
        col: 'md:col-span-5',
        icon: null,
        text: "Because platforms change. Algorithms change. Human behaviour doesn't.",
        highlight: true,
    },
    {
        col: 'md:col-span-5',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 15L7 9l4 3 5-7" stroke="#ceff1a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        text: 'By combining consumer psychology, data, and performance marketing, we build growth systems that generate measurable business outcomes and sustainable growth.',
        highlight: false,
    },
];

const container = {
    hidden: {},
    show: {
        transition: { staggerChildren: 0.12 },
    },
};

const item = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] } },
};

export default function PerformanceHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative bg-white pt-0 pb-0 overflow-hidden">
            <div
                className="relative w-full px-6 md:px-16 lg:px-24 pt-40 pb-24 overflow-hidden"
                style={{
                    background: '#000000',
                    borderBottomLeftRadius: '80px',
                    borderBottomRightRadius: '80px',
                }}
            >
                {/* Subtle noise texture */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
                }} />

                <div className="max-w-[1200px] mx-auto flex flex-col items-center gap-12">

                    {/* Eyebrow */}
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-sm md:text-base font-semibold tracking-[0.16em] uppercase text-center"
                        style={{ color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        Performance Marketing Agency in Bangalore
                    </motion.p>

                    {/* H1 */}
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight text-center"
                        style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            color: '#ffffff',
                        }}
                    >
                        Performance Marketing Built<br className="hidden md:block" /> Around Human Behaviour
                    </motion.h1>

                    {/* Creative card grid */}
                    <motion.div
                        variants={container}
                        initial="hidden"
                        animate="show"
                        className="w-full grid grid-cols-1 md:grid-cols-5 gap-3"
                    >
                        {cards.map((card, i) => (
                            <motion.div
                                key={i}
                                variants={item}
                                className={card.col}
                            >
                                {card.highlight ? (
                                    <div
                                        className="w-full rounded-2xl px-7 py-5 flex items-center justify-center"
                                        style={{
                                            background: 'rgba(206,255,26,0.08)',
                                            border: '1.5px solid rgba(206,255,26,0.3)',
                                        }}
                                    >
                                        <p
                                            className="text-center font-bold text-base md:text-lg leading-snug"
                                            style={{
                                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                                color: '#ceff1a',
                                                letterSpacing: '0.02em',
                                            }}
                                        >
                                            {card.text}
                                        </p>
                                    </div>
                                ) : (
                                    <div
                                        className="h-full rounded-2xl px-6 py-5 flex flex-col gap-4"
                                        style={{
                                            background: 'rgba(255,255,255,0.04)',
                                            border: '1px solid rgba(255,255,255,0.08)',
                                        }}
                                    >
                                        {card.icon && (
                                            <div
                                                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                                                style={{ background: 'rgba(206,255,26,0.08)' }}
                                            >
                                                {card.icon}
                                            </div>
                                        )}
                                        <p
                                            className="text-sm md:text-base leading-relaxed"
                                            style={{
                                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                                color: 'rgba(251,253,253,0.62)',
                                                letterSpacing: '0.03em',
                                            }}
                                        >
                                            {card.text}
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                    >
                        <button
                            onClick={openModal}
                            className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                            style={{
                                background: '#ceff1a',
                                color: '#080400',
                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                letterSpacing: '0.08em',
                            }}
                        >
                            Start a Project
                        </button>
                    </motion.div>

                </div>
            </div>
        </section>
    );
}
